import {z} from "zod";

import { FreeTextAnalysisFlag } from '@/types/enums/free-text-analysis'
import { FetchParams, FetchParamsWithRequiredOrderBy, orderByArray } from '@/types/search'
import {DATA_TABLE_RECORD_FETCH_LIMIT} from "@/utils/constants";

const orderDirectionSchema = z.enum(['ASC', 'DESC']);

const orderBySchema = z.object({
  column: z.enum(orderByArray),
  direction: orderDirectionSchema,
})

export const rootSearchSchema = z.object({
  order_by: orderBySchema.optional(),
  question_id: z.coerce.number().optional(),
  professional_id: z.coerce.number().optional(),
  tab: z.string().optional(),
  multiple_answer_choices: z.coerce.string().optional(),
  answer_choice: z.coerce.string().optional(),
  customer_email: z.string().optional(),
  customer_first_name: z.string().optional(),
  type: z.string().optional(),
  service_type_id: z.number().optional(),
  question_label: z.string().optional(),
  actioned: z.enum(["yes", "no"]).optional(),
  viewed: z.enum(["yes", "no"]).optional(),
  risk_id: z.coerce.number().optional(),
  opportunity_id: z.coerce.number().optional(),
  firm_id: z.coerce.number().optional(),
  redirect: z.string().optional(),
  data_value_type: z.string().optional(),
  sort_chart_by: z.string().optional(),
  period: z.string().optional(),
  client_email: z.string().optional(),
  client_first_name: z.string().optional(),
  report_type: z.string().optional(),
  enquiry_status: z.string().optional(),
  invite_type: z.string().optional(),
  flag: z.nativeEnum(FreeTextAnalysisFlag).optional(),
});

export type RootSearch = z.infer<typeof rootSearchSchema>;

//Verified adviser scores
export const defaultFetchParamsVerifiedByAdviser: FetchParamsWithRequiredOrderBy = {
  limit: 100000,
  offset: 0,
  order_by: {
    column: 'first_name',
    direction: 'ASC',
  },
}
export const defaultSearchVerifiedByAdviser: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'adviser_name',
    direction: 'ASC',
  },
}
export const defaultTabVerifiedByAdviser = 'profile-stats'
const verifiedByAdviserTabSchema = z.enum(['profile-stats', 'content-added-or-shared'])
export const verifiedAdviserScoresSearchSchema = z.object({
  order_by: orderBySchema.default(defaultSearchVerifiedByAdviser.order_by),
  limit: z.number().default(defaultSearchVerifiedByAdviser.limit),
  offset: z.number().default(defaultSearchVerifiedByAdviser.offset),
  tab: verifiedByAdviserTabSchema.catch(defaultTabVerifiedByAdviser),
})

export const mergedVerifiedAdviserScoresSearchSchema = rootSearchSchema.extend(verifiedAdviserScoresSearchSchema.shape);

export type VerifiedAdviserScoresSearch = z.infer<typeof mergedVerifiedAdviserScoresSearchSchema>;

export const defaultFetchParamsEnquiries: FetchParamsWithRequiredOrderBy = {
  limit: DATA_TABLE_RECORD_FETCH_LIMIT,
  offset: 0,
  order_by: {
    column: 'enquiry_id',
    direction: 'DESC',
  },
}

export const defaultSearchParamsEnquiries: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'enquiry_id',
    direction: 'DESC',
  },
}

export const enquiriesSearchSchema = z.object({
  order_by: orderBySchema.default(defaultSearchParamsEnquiries.order_by),
  limit: z.number().default(defaultSearchParamsEnquiries.limit),
  offset: z.number().default(defaultSearchParamsEnquiries.offset),
})

export const mergedEnquiriesSchema = rootSearchSchema.extend(enquiriesSearchSchema.shape);

export type EnquiriesSearch = z.infer<typeof mergedEnquiriesSchema>;

//firm-performance page schemas
export const firmPerformanceSearchSchema = z.object({
  tab: z.string().default('all'),
});

export const mergedFirmPerformanceSearchSchema = rootSearchSchema.extend(firmPerformanceSearchSchema.shape);

export type FirmPerformanceSearch = z.infer<typeof mergedFirmPerformanceSearchSchema>;

//Review data
export const defaultFetchParamsReviewData: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'submitted_at',
    direction: 'DESC',
  },
}

export const reviewDataSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsReviewData.order_by),
  limit: z.number().default(defaultFetchParamsReviewData.limit),
  offset: z.number().default(defaultFetchParamsReviewData.offset),
})

export const mergedReviewDataSchema = rootSearchSchema.extend(reviewDataSearchSchema.shape);

export type ReviewDataSearch = z.infer<typeof mergedReviewDataSchema>;

//Elevation by adviser
export const defaultFetchParamsElevationByAdviser: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'recommendations_per_client',
    direction: 'DESC',
  },
}

export const elevationByAdviserSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsElevationByAdviser.order_by),
  limit: z.number().default(defaultFetchParamsElevationByAdviser.limit),
  offset: z.number().default(defaultFetchParamsElevationByAdviser.offset),
})

export const mergedElevationByAdviserSchema = rootSearchSchema.extend(elevationByAdviserSearchSchema.shape);

export type ElevationByAdviserSearch = z.infer<typeof mergedElevationByAdviserSchema>;

//Advisers page
export const defaultFetchParamsAdviser: FetchParamsWithRequiredOrderBy = {
  limit: DATA_TABLE_RECORD_FETCH_LIMIT,
  offset: 0,
  order_by: {
    column: 'first_name',
    direction: 'ASC',
  },
}

export const defaultFetchParamsTopRatedAdvisers: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'adviser_name',
    direction: 'ASC',
  },
}

const topRatedAdvisersSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsTopRatedAdvisers.order_by),
  limit: z.number().default(defaultFetchParamsTopRatedAdvisers.limit),
  offset: z.number().default(defaultFetchParamsTopRatedAdvisers.offset),
})

export const mergedTopRatedAdvisersSchema = rootSearchSchema.extend(topRatedAdvisersSearchSchema.shape);
export type TopRatedAdviserSearch = z.infer<typeof mergedTopRatedAdvisersSchema>;

export const defaultSearchParamsAdviser: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'first_name',
    direction: 'ASC',
  },
}

export const adviserSearchSchema = z.object({
  order_by: orderBySchema.default(defaultSearchParamsAdviser.order_by),
  limit: z.number().default(defaultSearchParamsAdviser.limit),
  offset: z.number().default(defaultSearchParamsAdviser.offset),
})

export const mergedAdviserSchema = rootSearchSchema.extend(adviserSearchSchema.shape);

export type AdviserSearch = z.infer<typeof mergedAdviserSchema>;

//network firms performance page schemas
export const defaultFetchParamsNetworkFirmsPerformanceData: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'review_count',
    direction: 'DESC',
  },
}

export const networkFirmsPerformanceDataSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsNetworkFirmsPerformanceData.order_by),
  limit: z.number().default(defaultFetchParamsNetworkFirmsPerformanceData.limit),
  offset: z.number().default(defaultFetchParamsNetworkFirmsPerformanceData.offset),
})

export const mergedNetworkFirmsPerformanceDataSearchSchema = rootSearchSchema.extend(networkFirmsPerformanceDataSearchSchema.shape);

export type NetworkFirmsPerformanceDataSearch = z.infer<typeof mergedNetworkFirmsPerformanceDataSearchSchema>;

//Free text analysis
export const defaultFetchParamsFreeTextAnalysis: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'date',
    direction: 'DESC',
  },
}

export const freeTextAnalysisSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsFreeTextAnalysis.order_by),
  limit: z.number().default(defaultFetchParamsFreeTextAnalysis.limit),
  offset: z.number().default(defaultFetchParamsFreeTextAnalysis.offset),
})

export const mergedFreeTextAnalysisSearchSchema = rootSearchSchema.extend(freeTextAnalysisSearchSchema.shape);

export type FreeTextAnalysisSearch = z.infer<typeof mergedFreeTextAnalysisSearchSchema>;

// General performance page
export const defaultSearchParamsGeneralPerformance: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'last_login_date',
    direction: 'DESC',
  },
}

export const generalPerformanceSearchSchema = z.object({
  order_by: orderBySchema.default(defaultSearchParamsGeneralPerformance.order_by),
  limit: z.number().default(defaultSearchParamsGeneralPerformance.limit),
  offset: z.number().default(defaultSearchParamsGeneralPerformance.offset),
})

export const mergedGenetalPerformanceSchema = rootSearchSchema.extend(generalPerformanceSearchSchema.shape);

export type GeneralPerformaceSearch = z.infer<typeof mergedGenetalPerformanceSchema>;

//Risk and Opportunity Settings

export const defaultSearchParamsRiskAndOpportunitySettings = {
  tab: 'risks',
  limit: 25,
  offset: 0
}
export const riskAndOpportunitySettingsSearchSchema = z.object({
  tab: z.string().default(defaultSearchParamsRiskAndOpportunitySettings.tab),
  limit: z.number().default(defaultSearchParamsRiskAndOpportunitySettings.limit),
  offset: z.number().default(defaultSearchParamsRiskAndOpportunitySettings.offset),
});

export const mergedRiskAndOpportunitySettingsSearchSchema = rootSearchSchema.extend(riskAndOpportunitySettingsSearchSchema.shape);

export type RiskAndOpportunitySettingsSearch = z.infer<typeof mergedRiskAndOpportunitySettingsSearchSchema>;
//Client actions report
export const defaultCommonFetchParamsClientActionsReport: Omit<FetchParams, 'redirect'> = {
  limit: 25,
  offset: 0,
}

export const defaultFetchParamsReport: FetchParamsWithRequiredOrderBy = {
  ...defaultCommonFetchParamsClientActionsReport,
  ...{
    order_by: {
      column: 'identified',
      direction: 'DESC',
    }
  },
}

export const defaultFetchParamsReportRisksOrOpportunities: FetchParamsWithRequiredOrderBy = {
  ...defaultCommonFetchParamsClientActionsReport,
  ...{
    order_by: {
      column: 'id',
      direction: 'DESC',
    }
  },
}

export const reportSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsReport.order_by),
  limit: z.number().default(defaultFetchParamsReport.limit),
  offset: z.number().default(defaultFetchParamsReport.offset),
  tab: z.literal('report').default('report'),
})

export const reportRisksSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsReportRisksOrOpportunities.order_by),
  limit: z.number().default(defaultFetchParamsReportRisksOrOpportunities.limit),
  offset: z.number().default(defaultFetchParamsReportRisksOrOpportunities.offset),
  tab: z.literal('risks').default('risks'),
})

export const mergedReportRiskSchema = z.discriminatedUnion('tab', [
  reportSearchSchema,
  reportRisksSearchSchema,
]);

export type ClientActionsReportRiskSearch = z.infer<typeof mergedReportRiskSchema>;

export const reportOpportunitiesSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsReportRisksOrOpportunities.order_by),
  limit: z.number().default(defaultFetchParamsReportRisksOrOpportunities.limit),
  offset: z.number().default(defaultFetchParamsReportRisksOrOpportunities.offset),
  tab: z.literal('opportunities').default('opportunities'),
})

export const mergedReportOpportunitySchema = z.discriminatedUnion('tab', [
  reportSearchSchema,
  reportOpportunitiesSearchSchema,
]);

export type ClientActionsReportOpportunitySearch = z.infer<typeof mergedReportOpportunitySchema>;

export const defaultSearchTopRatedStatusCompareFirms: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'overall_rating_for_year',
    direction: 'DESC',
  },
}

export const topRatedStatusCompareFirmsSearchSchema = z.object({
  order_by: orderBySchema.default(defaultSearchTopRatedStatusCompareFirms.order_by),
  limit: z.number().default(defaultSearchTopRatedStatusCompareFirms.limit),
  offset: z.number().default(defaultSearchTopRatedStatusCompareFirms.offset),
})

export const mergedTopRatedStatusCompareFirmsSchema = rootSearchSchema.extend(topRatedStatusCompareFirmsSearchSchema.shape);

export type TopRatedStatusCompareFirmsSearch = z.infer<typeof mergedTopRatedStatusCompareFirmsSchema>;

export const defaultFetchParamsBulkInviteRecord: FetchParamsWithRequiredOrderBy = {
  limit: 10,
  offset: 0,
  order_by: {
    column: 'id',
    direction: 'DESC',
  },
}

//Invite history
export const defaultFetchParamsInviteHistory: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'created_at',
    direction: 'DESC',
  },
}

export const inviteHistorySearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsInviteHistory.order_by),
  limit: z.number().default(defaultFetchParamsInviteHistory.limit),
  offset: z.number().default(defaultFetchParamsInviteHistory.offset),
})

export const mergedInviteHistorySchema = rootSearchSchema.extend(inviteHistorySearchSchema.shape);

export type InviteHistorySearch = z.infer<typeof mergedInviteHistorySchema>;


//Invite requests
export const defaultFetchParamsInviteRequests: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'send_at',
    direction: 'DESC',
  },
}

export const inviteRequestsSearchSchema = z.object({
  order_by: orderBySchema.default(defaultFetchParamsInviteRequests.order_by),
  limit: z.number().default(defaultFetchParamsInviteRequests.limit),
  offset: z.number().default(defaultFetchParamsInviteRequests.offset),
})

export const mergedInviteRequestsSchema = rootSearchSchema.extend(inviteRequestsSearchSchema.shape);

export type InviteRequestsSearch = z.infer<typeof mergedInviteRequestsSchema>;

//Articles
export const defaultFetchParamsArticles: FetchParamsWithRequiredOrderBy = {
  limit: DATA_TABLE_RECORD_FETCH_LIMIT,
  offset: 0,
  order_by: {
    column: 'created_at',
    direction: 'DESC',
  },
}

export const defaultSearchParamsArticles: FetchParamsWithRequiredOrderBy = {
  limit: 25,
  offset: 0,
  order_by: {
    column: 'created_at',
    direction: 'DESC',
  },
}

export const articlesSearchSchema = z.object({
  order_by: orderBySchema.default(defaultSearchParamsArticles.order_by),
  limit: z.number().default(defaultSearchParamsArticles.limit),
  offset: z.number().default(defaultSearchParamsArticles.offset),
})

export const mergedArticlesSchema = rootSearchSchema.extend(articlesSearchSchema.shape);

export type ArticlesSearch = z.infer<typeof mergedArticlesSchema>;

//registration completion page schemas
export const registrationCompletionSearchSchema = z.object({
  token: z.coerce.string().optional(),
});

export type RegistrationCompletionSearch = z.infer<typeof registrationCompletionSearchSchema>;


export const defaultSearchParamsReportingDocumentsHistory: FetchParamsWithRequiredOrderBy = {
  order_by: {
    column: 'report_queue_date_period',
    direction: 'DESC',
  },
  limit: 25,
  offset: 0
}

export const reportingDocumentsSearchSchema = z.object({
  tab: z.literal('generate').default('generate'),
})

export const reportingDocumentsHistorySearchSchema = z.object({
  order_by: orderBySchema.default(defaultSearchParamsReportingDocumentsHistory.order_by),
  limit: z.number().default(defaultSearchParamsReportingDocumentsHistory.limit),
  offset: z.number().default(defaultSearchParamsReportingDocumentsHistory.offset),
  tab: z.literal('history').default('history'),
})

export const mergedReportingDocumentsSchema = z.discriminatedUnion('tab', [
  reportingDocumentsSearchSchema,
  reportingDocumentsHistorySearchSchema,
]);

export type ReportingDocumentsSearch = z.infer<typeof mergedReportingDocumentsSchema>;

